import { createContext, useCallback, useEffect, useRef, useState } from "react";
import axiosInstance, {
  isServerUnavailableError,
  setServerUnavailableHandler,
} from "../utils/axiosInstance";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ParseErrors } from "../utils/ParseErrors";
import PageLoader from "../components/PageLoader";

type userType = "super-admin" | "admin" | "agent";
type statusType = "invite-sent" | "verified";
type loginType = "local";

export type User = {
  id?: number;
  oAuthId?: string;
  name: string;
  code?: string;
  email: string;
  phone: string;
  password?: string;
  type: userType;
  status: statusType;
  logginType: loginType;
  collections?: string;
  collectionsPlan?: string;
  totalCollections?: string;
  createdAt?: string;
  updatedAt?: string;
};

type LoginUserInterface = {
  isLoggedIn: boolean;
  data: {
    token: string;
    user: User;
  };
};

type ValueAuthContext = {
  user: User | undefined;
  isLoading: boolean;
  UpdateLoginData: (loginData: LoginUserInterface) => void;
  HandleUserLogOut: () => void;
  pathName: string;
};

const unProtectedPages = ["/auth", "/reset"];

const isAuthPage = (path: string) =>
  unProtectedPages.some((page) => path === page || path.startsWith(`${page}/`));

export const AuthContext = createContext<ValueAuthContext | undefined>(
  undefined,
);

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<LoginUserInterface | undefined>();
  const [loading, setLoading] = useState(true);
  const navigation = useNavigate();
  const location = useLocation();
  const pathName = location.pathname;
  const pathNameRef = useRef(pathName);
  const lastToastAtRef = useRef(0);
  const skipSessionRestoreRef = useRef(false);

  pathNameRef.current = pathName;

  const UpdateLoginData = (loginData: LoginUserInterface) => {
    skipSessionRestoreRef.current = false;
    setUser(loginData);
  };

  const HandleNoSession = useCallback(
    (message = "Session Expired", serverUnavailable = false) => {
      if (serverUnavailable) {
        skipSessionRestoreRef.current = true;
      }

      if (!isAuthPage(pathNameRef.current)) {
        navigation("/auth", { replace: true });
        const now = Date.now();
        if (now - lastToastAtRef.current > 2000) {
          lastToastAtRef.current = now;
          toast.error(message, { toastId: "auth-redirect" });
        }
      }
      setUser(undefined);
      setLoading(false);
    },
    [navigation],
  );

  const UpdateUserSession = useCallback(async () => {
    if (isAuthPage(pathNameRef.current) && skipSessionRestoreRef.current) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await axiosInstance.get("/auth/login");
      if (res.data?.isLoggedIn === false) {
        HandleNoSession();
        return;
      }
      skipSessionRestoreRef.current = false;
      setUser(res.data);
      setLoading(false);
    } catch (error) {
      if (isServerUnavailableError(error)) {
        HandleNoSession(
          "Unable to reach the server. Please sign in again.",
          true,
        );
        return;
      }
      HandleNoSession();
    }
  }, [HandleNoSession]);

  useEffect(() => {
    UpdateUserSession();
  }, [pathName, UpdateUserSession]);

  useEffect(() => {
    setServerUnavailableHandler(() => {
      HandleNoSession("Unable to reach the server. Please sign in again.", true);
    });

    return () => setServerUnavailableHandler(null);
  }, [HandleNoSession]);

  const HandleUserLogOut = async () => {
    setLoading(true);
    try {
      await axiosInstance.get("/auth/logout");
      toast.success("Logged out successfully");
      setUser(undefined);
      navigation("/auth", { replace: true });
    } catch (error) {
      if (isServerUnavailableError(error)) {
        HandleNoSession("Unable to reach the server. Please sign in again.", true);
        return;
      }
      toast.error(ParseErrors(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      !loading &&
      user?.data?.user?.id &&
      isAuthPage(pathName) &&
      !skipSessionRestoreRef.current
    ) {
      navigation("/");
    }

    if (!loading && !user?.isLoggedIn && !isAuthPage(pathName)) {
      navigation("/auth", { replace: true });
    }
  }, [loading, user, navigation, pathName]);

  const onAuthPage = isAuthPage(pathName);

  if (loading && !user?.isLoggedIn) {
    return <PageLoader />;
  }

  if (!onAuthPage && !user?.isLoggedIn) {
    return <PageLoader />;
  }

  return (
    <AuthContext.Provider
      value={{
        pathName,
        UpdateLoginData,
        HandleUserLogOut,
        isLoading: loading,
        user: user?.data?.user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
